// Eskom PARCC — UI preference provider (the Legacy/Lumen theme axis).
//
// The app ships TWO named looks over one data layer, one route table and one
// permission model:
//   • Legacy — the current production UI. Default. Unchanged.
//   • Lumen  — the light-first redesign. Opt-in, per user.
//
// The choice is an attribute on <html> (`data-ui`), exactly as the existing
// light/dark theme is (`data-theme`, owned by lib/theme.tsx). Legacy CSS carries
// no `[data-ui]` prefix, so it keeps winning by default; every Lumen rule is
// prefixed and is therefore strictly more specific. Nothing about Legacy changes
// when Lumen lands.
//
// Three axes live here:
//   data-ui="legacy|lumen"                 — the theme
//   data-theme="light|dark"                — Lumen's sub-mode (delegated to lib/theme)
//   data-density="comfort|balanced|compact" — Lumen-only spacing
//
// PERSISTENCE. localStorage['parcc.prefs'] is the fast path and the anti-flash
// source (index.html reads it before the bundle paints). The same values mirror
// onto the signed-in user's `User` row through the existing, audited account
// endpoint (admin-api /api/account/profile), so the choice follows a person
// across devices. LOCAL WINS on conflict: the device the user just clicked on is
// the truth; the server copy only seeds a fresh browser.
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTheme, type Theme } from './theme';
import { useAuth } from './auth';
import { adminApi } from './adminApi';

export type UiVariant = 'legacy' | 'lumen';
export type Density = 'comfort' | 'balanced' | 'compact';
export type SidebarDefault = 'expanded' | 'collapsed';
/** The AI Compliance Engine surfaces, per person. Defaults 'off' — see below. */
export type AiMode = 'off' | 'on';

/** Every persisted UI preference. `theme` mirrors lib/theme's value so a single
 *  blob covers the anti-flash script and the server round-trip. */
export interface UiPrefs {
  variant: UiVariant;
  theme: Theme;
  density: Density;
  sidebarDefault: SidebarDefault;
  /** IANA zone for date/time rendering (Settings → Regional & format). */
  timeZone: string;
  /** BCP-47 locale for date + number grouping. NOT the Rand currency contract —
   *  see lib/money.ts, whose format the exports and parsers depend on. */
  numberFormat: string;
  /**
   * Whether this person has switched the AI Compliance Engine ON for themselves
   * (TK Jul 2026) — the fourth axis, and the same opt-in shape Lumen shipped with.
   *
   * THIS IS A PREFERENCE, NOT A PERMISSION. It lives in localStorage and is
   * user-writable, so it decides only what a person SEES. Eligibility is
   * `AccessGrant.aiEnabled`, re-derived server-side on every `/api/ai/*` call.
   * Never read this alone to gate a capability — use `useAiAvailability()` in
   * lib/aiMode, which combines both and cannot be spoofed from devtools.
   */
  aiMode: AiMode;
}

export const UI_PREFS_STORAGE_KEY = 'parcc.prefs';
/** The pre-Lumen theme key. Read once so nobody's dark mode resets on upgrade. */
const LEGACY_THEME_KEY = 'eskom-parcc:theme';

/** Legacy is the default: the redesign is opt-in and rolls out per user. */
export const DEFAULT_UI_PREFS: UiPrefs = {
  variant: 'legacy',
  theme: 'light',
  density: 'balanced',
  sidebarDefault: 'expanded',
  timeZone: 'Africa/Johannesburg',
  numberFormat: 'en-ZA',
  // AI is opt-in per person and rolls out one user at a time (Aziz and Lindiwe
  // first). Nobody is switched on by a deploy, and an absent key means off.
  aiMode: 'off',
};

const VARIANTS: readonly UiVariant[] = ['legacy', 'lumen'];
const DENSITIES: readonly Density[] = ['comfort', 'balanced', 'compact'];
const SIDEBAR_DEFAULTS: readonly SidebarDefault[] = ['expanded', 'collapsed'];
const THEMES: readonly Theme[] = ['light', 'dark'];
const AI_MODES: readonly AiMode[] = ['off', 'on'];

/** The time zones offered in Settings. Anything else is rejected to `null` on the
 *  server, so an unknown value can never reach the DB. */
export const TIME_ZONE_OPTIONS: readonly { value: string; label: string }[] = [
  { value: 'Africa/Johannesburg', label: 'South Africa Standard Time (SAST)' },
  { value: 'Africa/Lusaka', label: 'Central Africa Time (CAT) — Lusaka' },
  { value: 'Europe/London', label: 'United Kingdom (GMT/BST)' },
  { value: 'America/New_York', label: 'US Eastern (ET)' },
  { value: 'UTC', label: 'Coordinated Universal Time (UTC)' },
];

/** Date + number grouping locales offered in Settings. */
export const NUMBER_FORMAT_OPTIONS: readonly { value: string; label: string }[] = [
  { value: 'en-ZA', label: 'English (South Africa) — 31/07/2026 · 1 234 567' },
  { value: 'en-GB', label: 'English (United Kingdom) — 31/07/2026 · 1,234,567' },
  { value: 'en-US', label: 'English (United States) — 07/31/2026 · 1,234,567' },
];

/** Narrow an unknown value to one of `allowed`, else the fallback. Every stored
 *  or server-supplied preference goes through this, so a corrupted blob or a
 *  hand-edited DB row degrades to the default rather than reaching the DOM. */
function pick<T extends string>(value: unknown, allowed: readonly T[], fallback: T): T {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value)
    ? (value as T)
    : fallback;
}

function pickFrom(value: unknown, options: readonly { value: string }[], fallback: string): string {
  return typeof value === 'string' && options.some((o) => o.value === value) ? value : fallback;
}

/**
 * Resolve the starting preferences. PURE + side-effect-free so it is
 * unit-testable; the caller supplies the two stored strings.
 *
 * `stored` is the `parcc.prefs` JSON blob. `legacyTheme` is the pre-Lumen
 * `eskom-parcc:theme` value — honoured ONLY when the blob carries no theme, so
 * an existing user's dark mode survives the upgrade exactly once and is then
 * owned by the blob.
 */
export function resolveInitialPrefs(
  stored: string | null | undefined,
  legacyTheme?: string | null,
): UiPrefs {
  let raw: Record<string, unknown> = {};
  if (typeof stored === 'string' && stored !== '') {
    try {
      const parsed: unknown = JSON.parse(stored);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        raw = parsed as Record<string, unknown>;
      }
    } catch {
      /* corrupted blob — fall through to defaults */
    }
  }
  return {
    variant: pick(raw.variant, VARIANTS, DEFAULT_UI_PREFS.variant),
    theme: pick(
      raw.theme,
      THEMES,
      // Migration: no theme in the blob → inherit the pre-Lumen key.
      pick(legacyTheme, THEMES, DEFAULT_UI_PREFS.theme),
    ),
    density: pick(raw.density, DENSITIES, DEFAULT_UI_PREFS.density),
    sidebarDefault: pick(raw.sidebarDefault, SIDEBAR_DEFAULTS, DEFAULT_UI_PREFS.sidebarDefault),
    timeZone: pickFrom(raw.timeZone, TIME_ZONE_OPTIONS, DEFAULT_UI_PREFS.timeZone),
    numberFormat: pickFrom(raw.numberFormat, NUMBER_FORMAT_OPTIONS, DEFAULT_UI_PREFS.numberFormat),
    aiMode: pick(raw.aiMode, AI_MODES, DEFAULT_UI_PREFS.aiMode),
  };
}

/** Map a server profile row onto a preference patch, dropping unset/unknown
 *  values so a null column never overwrites a good local choice. */
export function prefsFromProfile(profile: {
  uiVariant?: string | null;
  uiTheme?: string | null;
  uiDensity?: string | null;
  sidebarDefault?: string | null;
  timeZone?: string | null;
  numberFormat?: string | null;
  aiMode?: string | null;
}): Partial<UiPrefs> {
  const patch: Partial<UiPrefs> = {};
  if (
    typeof profile.uiVariant === 'string' &&
    (VARIANTS as readonly string[]).includes(profile.uiVariant)
  ) {
    patch.variant = profile.uiVariant as UiVariant;
  }
  if (
    typeof profile.uiTheme === 'string' &&
    (THEMES as readonly string[]).includes(profile.uiTheme)
  ) {
    patch.theme = profile.uiTheme as Theme;
  }
  if (
    typeof profile.uiDensity === 'string' &&
    (DENSITIES as readonly string[]).includes(profile.uiDensity)
  ) {
    patch.density = profile.uiDensity as Density;
  }
  if (
    typeof profile.sidebarDefault === 'string' &&
    (SIDEBAR_DEFAULTS as readonly string[]).includes(profile.sidebarDefault)
  ) {
    patch.sidebarDefault = profile.sidebarDefault as SidebarDefault;
  }
  if (
    typeof profile.timeZone === 'string' &&
    TIME_ZONE_OPTIONS.some((o) => o.value === profile.timeZone)
  ) {
    patch.timeZone = profile.timeZone;
  }
  if (
    typeof profile.numberFormat === 'string' &&
    NUMBER_FORMAT_OPTIONS.some((o) => o.value === profile.numberFormat)
  ) {
    patch.numberFormat = profile.numberFormat;
  }
  if (typeof profile.aiMode === 'string' && (AI_MODES as readonly string[]).includes(profile.aiMode)) {
    patch.aiMode = profile.aiMode as AiMode;
  }
  return patch;
}

function readStored(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

interface UiPrefsCtx extends UiPrefs {
  /** Patch one or more preferences (persists locally + mirrors to the server). */
  setPrefs: (patch: Partial<UiPrefs>) => void;
  /** Convenience: flip Legacy ↔ Lumen. */
  toggleVariant: () => void;
  /** True while the redesign is active — the branch point for the shell. */
  isLumen: boolean;
}

const Ctx = createContext<UiPrefsCtx | null>(null);

export function UiPrefsProvider({ children }: { children: ReactNode }): React.JSX.Element {
  // `theme` is owned by lib/theme.tsx (it writes data-theme + its own key). We
  // mirror it into the blob so the anti-flash script and the server copy carry a
  // complete picture, but there is still exactly ONE writer of data-theme.
  const { theme, setTheme } = useTheme();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const [prefs, setPrefsState] = useState<UiPrefs>(() =>
    resolveInitialPrefs(readStored(UI_PREFS_STORAGE_KEY), readStored(LEGACY_THEME_KEY)),
  );

  // Apply the migrated theme once on mount: if the blob asked for dark and
  // lib/theme resolved to something else, the blob wins (it is the newer record).
  const migratedTheme = useRef(false);
  useEffect(() => {
    if (migratedTheme.current) return;
    migratedTheme.current = true;
    if (prefs.theme !== theme) setTheme(prefs.theme);
    // Intentionally mount-only: afterwards `theme` is the single source of truth.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the blob's theme in step with lib/theme (the header toggle writes there).
  const effective: UiPrefs = useMemo(() => ({ ...prefs, theme }), [prefs, theme]);

  // Stamp <html> and persist. data-theme is deliberately NOT written here.
  useEffect(() => {
    const root = document.documentElement;
    root.dataset.ui = effective.variant;
    root.dataset.density = effective.density;
    try {
      localStorage.setItem(UI_PREFS_STORAGE_KEY, JSON.stringify(effective));
    } catch {
      /* ignore quota / private-mode failures */
    }
  }, [effective]);

  // Seed from the server ONCE per signed-in session, for a browser that has no
  // local blob yet. A device that already carries a choice is never overridden.
  const hadLocal = useRef(readStored(UI_PREFS_STORAGE_KEY) !== null);
  const profileQuery = useQuery({
    queryKey: ['accountProfile'],
    queryFn: () => adminApi.getProfile(),
    enabled: Boolean(user) && !hadLocal.current,
    staleTime: 5 * 60_000,
    retry: false,
  });
  const seeded = useRef(false);
  useEffect(() => {
    if (seeded.current || hadLocal.current || !profileQuery.data) return;
    seeded.current = true;
    const patch = prefsFromProfile(profileQuery.data);
    if (Object.keys(patch).length === 0) return;
    if (patch.theme && patch.theme !== theme) setTheme(patch.theme);
    setPrefsState((p) => ({ ...p, ...patch }));
  }, [profileQuery.data, theme, setTheme]);

  const setPrefs = useCallback(
    (patch: Partial<UiPrefs>) => {
      // Theme routes through lib/theme so data-theme keeps one writer.
      if (patch.theme && patch.theme !== theme) setTheme(patch.theme);
      // Stamp <html> SYNCHRONOUSLY, not only in the effect below. React runs a
      // child's effects before its parent's, so a descendant that reads computed
      // styles on re-render (lib/useThemeColors resolves the Recharts palette off
      // <html>) would otherwise sample the OLD variant's tokens for one frame.
      if (typeof document !== 'undefined') {
        const root = document.documentElement;
        if (patch.variant) root.dataset.ui = patch.variant;
        if (patch.density) root.dataset.density = patch.density;
      }
      setPrefsState((p) => {
        const next = { ...p, ...patch, theme: patch.theme ?? theme };
        // Mirror to the User row — best effort. A failed mirror must never block
        // the click: the local choice has already applied.
        void adminApi
          .updateProfile({
            uiVariant: next.variant,
            uiTheme: next.theme,
            uiDensity: next.density,
            sidebarDefault: next.sidebarDefault,
            timeZone: next.timeZone,
            numberFormat: next.numberFormat,
            aiMode: next.aiMode,
          })
          .then(() => queryClient.invalidateQueries({ queryKey: ['accountProfile'] }))
          .catch(() => {
            /* offline / signed out — localStorage still holds the choice */
          });
        return next;
      });
    },
    [theme, setTheme, queryClient],
  );

  const toggleVariant = useCallback(
    () => setPrefs({ variant: effective.variant === 'lumen' ? 'legacy' : 'lumen' }),
    [effective.variant, setPrefs],
  );

  const value = useMemo<UiPrefsCtx>(
    () => ({ ...effective, setPrefs, toggleVariant, isLumen: effective.variant === 'lumen' }),
    [effective, setPrefs, toggleVariant],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** Access the UI preferences. Throws outside a {@link UiPrefsProvider}. */
export function useUiPrefs(): UiPrefsCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useUiPrefs must be used within a <UiPrefsProvider>');
  return ctx;
}

/**
 * This person's AI switch, safe OUTSIDE the provider (unit tests render pages
 * bare, exactly as {@link useVariant} handles). Returns `'off'` with no provider
 * — fail-closed, so a component rendered without preferences never shows AI.
 *
 * Deliberately returns the RAW preference and nothing else. Gate UI on
 * `useAiAvailability()` in lib/aiMode, which also requires the server-side grant;
 * this alone is user-writable from devtools and authorises nothing.
 */
export function useAiModePref(): AiMode {
  const ctx = useContext(Ctx);
  return ctx ? ctx.aiMode : 'off';
}

/**
 * The active variant, safe OUTSIDE the provider. Shared primitives (Select,
 * Pager, DetailSurface …) are rendered by pages that are always inside the
 * provider in the app, but are also rendered bare in unit tests — falling back
 * to the DOM attribute keeps them usable in both without a mandatory wrapper.
 */
export function useVariant(): UiVariant {
  const ctx = useContext(Ctx);
  const [domVariant, setDomVariant] = useState<UiVariant>(() =>
    typeof document === 'undefined'
      ? 'legacy'
      : pick(document.documentElement.dataset.ui, VARIANTS, 'legacy'),
  );
  useEffect(() => {
    if (ctx || typeof document === 'undefined' || typeof MutationObserver === 'undefined') return;
    const root = document.documentElement;
    const sync = (): void => setDomVariant(pick(root.dataset.ui, VARIANTS, 'legacy'));
    sync();
    const observer = new MutationObserver(sync);
    observer.observe(root, { attributes: true, attributeFilter: ['data-ui'] });
    return () => observer.disconnect();
  }, [ctx]);
  return ctx ? ctx.variant : domVariant;
}
